import { upgradeToAndCall } from '../operations';
import { needsUpgrade } from '../utils/needsUpgrade';
import { selectUserOpsByAddress, userOpsStore } from '../store/userOpsStore';
import { getUserOperationReceipt } from './getUserOperationReceipt';
import { sendUserOperationWithRetry } from './sendUserOperationWithRetry';
import { getCallDataForUserop } from './getCallDataForUserOp';
import { prepareAndPrompt } from './preparation';
import { eth_sendUserOperation } from './eth_sendUserOperation';
export async function sendUserOp(args) {
    const { functionHashForPaymasterProxy, spaceId, retryCount, signer, toAddress, callData, value, skipConfirmation, newAccountImplementationType, smartAccountSession, enableUsdc, } = args;
    let smartAccount = await smartAccountSession.getSmartAccount({ signer });
    const _needsUpgrade = needsUpgrade(newAccountImplementationType, smartAccount.type);
    if (_needsUpgrade &&
        ((typeof value === 'bigint' && value > 0n) ||
            (Array.isArray(value) && value.some((v) => v > 0n)))) {
        // most userops can be batched with the upgrade op.
        // but if the op is passing a value (tipping, joining a paid space, etc), we can't encodeExecuteBatch with values on a simple account, so we need to upgrade first
        // upgradeToAndCall recursively calls sendUserOp, but since that op has no value, it won't match this block and will instead skip to below
        const upgradeOp = await upgradeToAndCall({
            smartAccount,
            signer: args.signer,
        });
        const receipt = await upgradeOp.getUserOperationReceipt();
        if (!receipt) {
            throw new Error('Upgrade to waiting for receipt timed out');
        }
        if (!receipt.success) {
            throw new Error('Upgrade to failed');
        }
        await smartAccountSession.reset();
        smartAccount = await smartAccountSession.getSmartAccount({ signer });
    }
    const sender = smartAccount.address;
    console.log(`[UserOperations] debug::callData`, {
        sender,
        toAddress,
        callData,
        value,
        needsUpgrade: _needsUpgrade,
    });
    // always reset prompt response
    userOpsStore.getState().setPromptResponse(sender, undefined);
    const resetUseropStore = () => {
        console.log('[UserOperations] resetting user op store');
        const { setCurrent, reset } = userOpsStore.getState();
        reset(sender);
        setCurrent({
            sender,
            op: undefined,
            functionHashForPaymasterProxy: functionHashForPaymasterProxy,
            spaceId: spaceId,
            tokenQuote: undefined,
        });
    };
    const pendingUserOp = selectUserOpsByAddress(sender).pending;
    if (pendingUserOp.hash) {
        console.log('[UserOperations] pending user op found, checking if it has landed...');
        try {
            // check if the pending op has landed
            const result = await getUserOperationReceipt({
                bundlerClient: smartAccount.client,
                userOpHash: pendingUserOp.hash,
            });
            if (result) {
                console.log('[UserOperations] previously pending user op landed', result);
                resetUseropStore();
            }
            else {
                console.log('[UserOperations] previously pending user op has not landed');
                const { setCurrent } = userOpsStore.getState();
                setCurrent({
                    sender,
                    op: undefined,
                    functionHashForPaymasterProxy,
                    spaceId,
                    tokenQuote: undefined,
                });
            }
        }
        catch (error) {
            // TODO: retry getUserOperationReceipt
            console.log('[UserOperations] error getting user operation receipt', error);
            resetUseropStore();
        }
    }
    else {
        resetUseropStore();
    }
    let opResponse;
    // throws if the userop is rejected by the bundler
    if (callData) {
        let normalizedCallData;
        if (Array.isArray(toAddress) && Array.isArray(callData)) {
            const _value = Array.isArray(value) ? value : toAddress.map(() => value ?? 0n);
            normalizedCallData = await getCallDataForUserop({
                toAddress,
                callData,
                value: _value,
                smartAccount,
                signer,
                needsUpgrade: _needsUpgrade,
                functionHash: args.functionHashForPaymasterProxy,
            });
        }
        else if (typeof toAddress === 'string' && typeof callData === 'string') {
            const _value = typeof value === 'bigint' ? value : undefined;
            normalizedCallData = await getCallDataForUserop({
                toAddress,
                callData,
                value: _value,
                smartAccount,
                signer,
                needsUpgrade: _needsUpgrade,
                functionHash: args.functionHashForPaymasterProxy,
            });
        }
        else {
            throw new Error('[sendUserop]::Invalid arguments');
        }
        opResponse = await sendUserOperationWithRetry({
            smartAccountAddress: sender,
            sendUserOperation: async () => {
                const preparedUserOp = await prepareAndPrompt({
                    smartAccount,
                    skipConfirmation,
                    callData: normalizedCallData,
                    enableUsdc,
                });
                if (!smartAccount.client.account) {
                    throw new Error('Smart account client account is undefined');
                }
                const userOpHash = await eth_sendUserOperation(smartAccount.client, preparedUserOp);
                return smartAccount.getUserOperationResponse({
                    userOpHash,
                });
            },
            retryCount,
        });
    }
    else {
        throw new Error('callData is required');
    }
    // if op made it to the bundler, copy to pending
    userOpsStore.getState().setPending({
        sender,
        hash: opResponse.userOpHash,
    });
    console.log('[UserOperations] set pending user op', userOpsStore.getState().userOps[sender].pending);
    if (_needsUpgrade) {
        await smartAccountSession.reset();
    }
    return {
        userOpHash: opResponse.userOpHash,
        wait: async () => {
            return opResponse.wait();
        },
        getUserOperationReceipt: async () => {
            const receipt = await opResponse.getUserOperationReceipt();
            return receipt;
        },
    };
}
